import { v as t, i as dateFormatNumeric, u as datetimeFormatNumeric, w as bignumber, n as numberFormat } from './i18n-9ea5877b.js';

// Utility function to sign validation with the appropriate return value: t() string or undefined
const signReturn = (predicate, i18nPath, parameters) => predicate ? t(i18nPath, parameters) : undefined;
/* istanbul ignore next */
// Utility function to display any value as a string for developers, *do not* use this for presentation
const valueAsText = (value) => {
  if (typeof value === 'undefined')
    return 'undefined';
  if (typeof value === 'number' && isNaN(value))
    return 'NaN';
  if (value === null)
    return 'null';
  if (typeof value === 'boolean')
    return value ? 'true' : 'false';
  if (typeof value === 'function')
    return value.toString();
  if (value instanceof RegExp)
    return `/${value.source}/${value.flags}`;
  if (value instanceof Date || value.hasOwnProperty('toISOString'))
    return value.toISOString();
  if (typeof File !== 'undefined' && value instanceof File)
    return `File { name: ${valueAsText(value.name)}, size: ${valueAsText(value.size)}, lastModified: ${valueAsText(value.lastModified)}, type: ${valueAsText(value.type)} }`;
  if (typeof value === 'string' || typeof value === 'number')
    return JSON.stringify(value);
  if (Array.isArray(value))
    return `[${value.map(valueAsText).join(',')}]`;
  if (value instanceof HTMLElement)
    return `<${value.tagName.toLowerCase()} />`;
  if (typeof value === 'object') {
    let returns = [];
    for (let key of Object.keys(value)) {
      returns.push(`${key}: ${valueAsText(value[key])}`);
    }
    return `{ ${returns.join(', ')} }`;
  }
  return 'Add return type to validate.valueAsText()';
};
// String validation
const vString = (value) => signReturn(typeof value !== 'string', 'validation.string', { value });
const vMinLength = (minLength) => (value) => signReturn(vString(value) || value.length < minLength, 'validation.minLength', {
  minLength,
  length: typeof value === 'string' ? value.length : undefined,
  value
});
const vMaxLength = (maxLength) => (value) => signReturn(vString(value) || value.length > maxLength, 'validation.maxLength', {
  maxLength,
  length: typeof value === 'string' ? value.length : undefined,
  value
});
// case insensitive group match
const vOneOfI = (options) => {
  let optionsI = options.map(option => option.toLocaleLowerCase());
  return (value) => signReturn(vString(value) || !optionsI.includes(value.toLocaleLowerCase()), 'validation.oneOfI', { options: options.join(', '), value });
};
const vPattern = (pattern) => (value) => signReturn(vString(value) || !pattern.test(value), 'validation.pattern', { pattern: valueAsText(pattern), value });
const vAlpha = (value) => signReturn(vPattern(/^[A-Z]+$/i)(value), 'validation.alpha', { value });
const vAlphaNumeric = (value) => signReturn(vPattern(/^[A-Z0-9_]+$/i)(value), 'validation.alphaNumeric', { value });
const vPartialHex = (maxLength) => (value) => signReturn(vPattern(new RegExp(`^(?:0x)?[A-F0-9]{1,${maxLength}}$`, 'i'))(value), 'validation.partialHex', { maxLength, value });
const vIncludeUppercase = (value) => signReturn(vPattern(/[A-Z]/)(value), 'validation.includeUppercase', { value });
const vIncludeLowercase = (value) => signReturn(vPattern(/[a-z]/)(value), 'validation.includeLowercase', { value });
const vIncludeNumber = (value) => signReturn(vPattern(/[0-9]/)(value), 'validation.includeNumber', { value });
const emailRegex = /^(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/i;
const vEmail = (value) => signReturn(vPattern(emailRegex)(value), 'validation.email', { value });
const vFilename = (value) => signReturn(vPattern(/^[^\\\/:\*\?"\<\>\|]+$/)(value), 'validation.filename', { value });
const vURL = (value) => signReturn(vPattern(/^((http|https):\/\/.)[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)$/)(value), 'validation.url', { value });
// mostly taken from https://rgxdb.com/r/2Z7DWG3I
const postCodeRegex = new Map([
  ['aus', /^(?:(?:[2-8]\d|9[0-7]|0?[28]|0?9(?=09))(?:\d{2}))$/],
  ['bel', /^(?:(?:[1-9])(?:\d{3}))$/],
  ['bra', /^\d{5}-?\d{3}$/],
  ['can', /^(?:[ABCEGHJ-NPRSTVXY]\d[A-Z][ -]?\d[A-Z]\d)$/i],
  ['dnk', /^(?:[1-24-9]\d{3}|3[0-8]\d{2})$/],
  ['fra', /^(?:0[1-9]|[1-8]\d|9[0-8])\d{3}$/],
  ['deu', /^\d{5}$/],
  ['grl', /^39\d{2}$/],
  ['ita', /^\d{5}$/],
  ['nld', /^(?:NL-)?(?:[1-9]\d{3} ?(?:[A-EGHJ-NPRTVWXZ][A-EGHJ-NPRSTVWXZ]|S[BCEGHJ-NPRTVWXZ]))$/i],
  ['nor', /^\d{4}$/],
  ['lux', /^L-\d{4}$/i],
  ['esp', /^(?:0[1-9]|[1-4]\d|5[0-2])\d{3}$/],
  ['che', /^[1-9]\d{3}$/],
  ['gbr', /^(([A-Z]{1,2}[0-9][A-Z0-9]?|ASCN|STHL|TDCU|BBND|[BFS]IQQ|PCRN|TKCA) ?[0-9][A-Z]{2}|BFPO ?[0-9]{1,4}|(KY[0-9]|MSR|VG|AI)[ -]?[0-9]{4}|[A-Z]{2} ?[0-9]{2}|GE ?CX|GIR ?0A{2}|SAN ?TA1)$/i],
  ['usa', /^\d{5}(?:-\d{4})?$/],
]);
const vPostcode = (alpha3) => (value) => {
  let regex = postCodeRegex.get(alpha3.toLowerCase());
  if (!regex)
    regex = /^.+$/;
  return signReturn(vPattern(regex)(value), 'validation.postcode', { alpha3, value });
};
// ethereum strings
const ethereumAddressRegex = /^0x[A-F0-9]{40}$/i;
const vEthereumAddress = (value) => signReturn(vPattern(ethereumAddressRegex)(value), 'validation.ethereumAddress', { value });
const vWalletAddress = (value) => signReturn(vPattern(ethereumAddressRegex)(value), 'validation.walletAddress', { value });
const vTokenAddress = (value) => signReturn(vPattern(ethereumAddressRegex)(value), 'validation.tokenAddress', { value });
const vTransactionHash = (value) => signReturn(vPattern(/^0x([A-F0-9]{40,})$/i)(value), 'validation.transactionHash', { value });
// date strings YYYY-MM-DD short ISO
const vDate = (value) => signReturn(vPattern(/^\d{4}-([0]\d|1[0-2])-([0-2]\d|3[01])$/)(value), 'validation.date', { value });
// remove dashes and turn to int for comparison
const dateAsInt = (date) => parseInt(date.replace(/\D+/g, ''));
const vDateMin = (min) => {
  const minAsInt = dateAsInt(min);
  const minLocalised = dateFormatNumeric(new Date(Date.parse(min)), true);
  return (value) => signReturn(vDate(value) || dateAsInt(value) < minAsInt, 'validation.dateMin', { min: minLocalised, value });
};
const vDateMax = (max) => {
  const maxAsInt = dateAsInt(max);
  const maxLocalised = dateFormatNumeric(new Date(Date.parse(max)), true);
  return (value) => signReturn(vDate(value) || dateAsInt(value) > maxAsInt, 'validation.dateMax', { max: maxLocalised, value });
};
// date time strings YYYY-MM-DDTHH:MM:SS
const vDatetime = (value) => signReturn(vPattern(/^\d{4}-([0]\d|1[0-2])-([0-2]\d|3[01])T([01]\d|2[0-3]):[0-5]\d:[0-5]\d$/)(value), 'validation.datetime', { value });
const vDatetimeMin = (min) => {
  const minAsInt = dateAsInt(min);
  const minLocalised = datetimeFormatNumeric(new Date(Date.parse(min + '.000Z')), true);
  return (value) => {
    return signReturn(vDatetime(value) || dateAsInt(value) < minAsInt, 'validation.datetimeMin', { min: minLocalised, value });
  };
};
const vDatetimeMax = (max) => {
  const maxAsInt = dateAsInt(max);
  const maxLocalised = datetimeFormatNumeric(new Date(Date.parse(max + '.000Z')), true);
  return (value) => signReturn(vDatetime(value) || dateAsInt(value) > maxAsInt, 'validation.datetimeMax', { max: maxLocalised, value });
};
// remove html tag like things
const stripTags = (value) => value.replace(/(<([^>]+)>)/gi, '');
const vHtmlMinLength = (minLength) => (value) => signReturn(vString(value) || stripTags(value).length < minLength, 'validation.htmlMinLength', { minLength, value });
const vEmpty = (value) => signReturn(value !== null && value !== '', 'validation.empty', { value });
const vEmptyOr = (vFunc) => (value) => signReturn(vEmpty(value) && vFunc(value), 'validation.emptyOr', { value });
const vRequired = (value) => signReturn(value === '' || value === undefined || value === null, 'validation.required', { value });
// Big Number validation
const vBigNumber = (decimals) => (value) => signReturn(vPattern(new RegExp('^-?\\d+(?:\\.\\d{0,' + decimals + '})?$'))(value), 'validation.bigNumber', { decimals, value });
const vMinBigNumber = (minNumber, decimals = 18) => {
  const minBN = new bignumber.BigNumber(minNumber, 10);
  return (value) => signReturn(vBigNumber(decimals)(value) || minBN.isGreaterThan(value, 10), 'validation.minNumber', { minNumber: numberFormat(minBN, decimals), value });
};
const vGreaterThanBigNumber = (minNumber, decimals = 18) => {
  const minBN = new bignumber.BigNumber(minNumber, 10);
  return (value) => signReturn(vBigNumber(decimals)(value) || minBN.isGreaterThanOrEqualTo(value, 10), 'validation.greaterThanNumber', { minNumber: numberFormat(minBN, decimals), value });
};
const vMaxBigNumber = (maxNumber, decimals = 18) => {
  const maxBN = new bignumber.BigNumber(maxNumber, 10);
  return (value) => signReturn(vBigNumber(decimals)(value) || maxBN.isLessThan(value, 10), 'validation.maxNumber', { maxNumber: numberFormat(maxBN, decimals), value });
};
const vLessThanBigNumber = (maxNumber, decimals = 18) => {
  const maxBN = new bignumber.BigNumber(maxNumber, 10);
  return (value) => signReturn(vBigNumber(decimals)(value) || maxBN.isLessThanOrEqualTo(value, 10), 'validation.lessThanNumber', { maxNumber: numberFormat(maxBN, decimals), value });
};
// Number validation
const vNumber = (value) => signReturn(typeof value !== 'number' || isNaN(value), 'validation.number', { value });
const vMinNumber = (minNumber) => (value) => signReturn(vNumber(value) || minNumber > value, 'validation.minNumber', { minNumber, value });
const vGreaterThanNumber = (minNumber) => (value) => signReturn(vNumber(value) || minNumber >= value, 'validation.greaterThanNumber', { minNumber, value });
const vMaxNumber = (maxNumber) => (value) => signReturn(vNumber(value) || maxNumber < value, 'validation.maxNumber', { maxNumber, value });
const vLessThanNumber = (maxNumber) => (value) => signReturn(vNumber(value) || maxNumber <= value, 'validation.lessThanNumber', { maxNumber, value });
const vInteger = (value) => signReturn(vNumber(value) || !Number.isInteger(value), 'validation.integer', { value });
const vDecimal = (decimals) => (value) => signReturn(vNumber(value) || (Math.floor(value) !== value && value.toString().split('.')[1].length > decimals), 'validation.decimal', { decimals, value });
// Boolean validation
const vBoolean = (value) => signReturn(typeof value !== 'boolean', 'validation.boolean', { value });
const vTrue = (value) => signReturn(vBoolean(value) || value !== true, 'validation.true', { value });
// The oddball
const vOneOf = (options) => (value) => signReturn(!options.includes(value), 'validation.oneOf', { options: options.join(', '), value });
// Array validation
const vArray = (value) => signReturn(!Array.isArray(value), 'validation.array', { value });
const vArrayMinLength = (minLength) => (value) => signReturn(vArray(value) || value.length < minLength, 'validation.arrayMinLength', {
  minLength,
  length: Array.isArray(value) ? value.length : undefined,
  value
});
const vArrayMaxLength = (maxLength) => (value) => signReturn(vArray(value) || value.length > maxLength, 'validation.arrayMaxLength', {
  maxLength,
  length: Array.isArray(value) ? value.length : undefined,
  value
});
const vArrayOf = (vFunc) => (value) => signReturn(vArray(value) || value.findIndex(vFunc) !== -1, 'validation.arrayOf', { value });
// HTML Element validation
const vElement = (value) => signReturn(!(value instanceof HTMLElement)
  && vObjectWith({
    '$tag$': vString,
    '$flags$': vNumber,
    '$text$': vNullOr(vString),
    '$elm$': vNullOr(vObject),
    '$attrs$': vNullOr(vObject)
  })(value), 'validation.element');
// Object validation
const vObject = (value) => signReturn(!vArray(value) || !vNull(value) || !vDateObject(value) || value instanceof HTMLElement || typeof value !== 'object', 'validation.object', { value });
const vObjectWith = (typing) => (value) => {
  const signedReturn = (invalidKey) => signReturn(true, 'validation.objectWith', { value, invalidKey });
  if (vObject(value))
    return signedReturn();
  for (let key of Object.keys(typing)) {
    const vFunc = typing[key];
    if (vFunc(value[key]))
      return signedReturn(key);
  }
};
// Null validation for form value @Prop value
const vNull = (value) => signReturn(value !== null, 'validation.null', { value });
const vNullOr = (vFunc) => (value) => signReturn(vNull(value) && vFunc(value), 'validation.nullOr', { value });
// Undefined validation for optional @Props
const vUndef = (value) => signReturn(typeof value !== 'undefined', 'validation.undef', { value });
const vUndefOr = (vFunc) => (value) => signReturn(vUndef(value) && vFunc(value), 'validation.undefOr', { value });
// Function validation
const vFunction = (value) => signReturn(typeof value !== 'function', 'validation.function', { value });
// Date object validation
const vDateObject = (value) => signReturn(!(value instanceof Date), 'validation.dateObject', { value });

export { vNullOr as $, vHtmlMinLength as A, vEmpty as B, vEmptyOr as C, vRequired as D, vBigNumber as E, vMinBigNumber as F, vGreaterThanBigNumber as G, vMaxBigNumber as H, vLessThanBigNumber as I, vNumber as J, vMinNumber as K, vGreaterThanNumber as L, vMaxNumber as M, vLessThanNumber as N, vInteger as O, vDecimal as P, vBoolean as Q, vTrue as R, vOneOf as S, vArray as T, vArrayMinLength as U, vArrayMaxLength as V, vArrayOf as W, vElement as X, vObject as Y, vObjectWith as Z, vNull as _, vString as a, vUndef as a0, vUndefOr as a1, vFunction as a2, vDateObject as a3, vMinLength as b, vMaxLength as c, vOneOfI as d, vPattern as e, vAlpha as f, vAlphaNumeric as g, vPartialHex as h, vIncludeUppercase as i, vIncludeLowercase as j, vIncludeNumber as k, vEmail as l, vFilename as m, vURL as n, vPostcode as o, vEthereumAddress as p, vWalletAddress as q, vTokenAddress as r, vTransactionHash as s, vDate as t, vDateMin as u, valueAsText as v, vDateMax as w, vDatetime as x, vDatetimeMin as y, vDatetimeMax as z };
